import { proxy } from "valtio"
import { Spektrix } from "../../server/lib/spektrix-types"
import { checkoutStore } from "@features/tickets/checkout-store"

export type NewAccount = Pick<Spektrix.NewCustomer, "firstName" | "lastName" | "email" | "password">

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Spektrix.HttpError ? error.message : fallback
}

/** Expanded on every customer read, so the account panel can show the customer's memberships. */
const CUSTOMER_EXPAND = "subscriptions"
/** Matches the basket's expansion, so booked tickets carry their event, session and ticket type. */
const ORDER_EXPAND = "tickets/event,tickets/instance,tickets/type"

export type CustomerDetails = Pick<Spektrix.CustomerPatch, "firstName" | "lastName" | "email" | "mobile" | "phone">

function createAccountStore() {
  let baseUrl: string | undefined
  let customerLoad: Promise<void> | undefined
  let membershipNamesLoad: Promise<void> | undefined
  // Sign in and sign out take precedence over a customer read that was already in flight.
  let sessionRevision = 0

  function createClient() {
    if (!baseUrl) throw new Error("Your account could not be reached. Please reload and try again.")
    return Spektrix.createClient({ baseUrl, credentials: "include" })
  }

  async function readCustomer() {
    const revision = sessionRevision
    try {
      const customer = await createClient().GET("v3/customer", { expand: CUSTOMER_EXPAND })
      // Keep the current snapshot when nothing changed, so repeat checks don't re-render.
      if (revision === sessionRevision && JSON.stringify(customer) !== JSON.stringify(store.customer)) {
        store.customer = customer
      }
    } catch (error) {
      // Spektrix responds 401 when no customer is signed in. Other failures keep the last known
      // customer; signing in from the form reports them.
      if (revision === sessionRevision && error instanceof Spektrix.HttpError && error.status === 401) {
        store.customer = null
      }
    } finally {
      store.isLoaded = true
    }
  }

  async function changeSession(action: (client: Spektrix.Client) => Promise<Spektrix.Customer | null>) {
    if (store.isPending) return
    sessionRevision++
    store.isPending = true
    try {
      store.customer = await action(createClient())
      store.isLoaded = true
    } finally {
      store.isPending = false
    }
    // The Spektrix basket belongs to the session, so refresh it for the new customer.
    void checkoutStore.refreshBasket()
  }

  const store = proxy({
    customer: null as Spektrix.Customer | null,
    isLoaded: false,
    isPending: false,
    /** The token from the password reset link the customer arrived with. Spektrix tokens are single use. */
    passwordResetToken: null as string | null,
    /** Membership names by ID, from the public membership list. */
    membershipNames: null as Record<string, string> | null,
    /** Reads the session's customer. Concurrent calls share one request. */
    loadCustomer(url: string) {
      baseUrl = url
      customerLoad ??= readCustomer().finally(() => {
        customerLoad = undefined
      })
      return customerLoad
    },
    /** A customer's subscriptions only identify their memberships, so names come from the public list. */
    loadMembershipNames() {
      membershipNamesLoad ??= createClient()
        .GET("v3/memberships", {})
        .then((memberships) => {
          store.membershipNames = Object.fromEntries(memberships.map((membership) => [membership.id, membership.name]))
        })
        .catch(() => {
          // Allow a later attempt.
          membershipNamesLoad = undefined
        })
      return membershipNamesLoad
    },
    /** Re-reads the session's customer from the API address of the last load. */
    refreshCustomer() {
      return baseUrl ? store.loadCustomer(baseUrl) : Promise.resolve()
    },
    async signIn(credentials: Spektrix.Credentials) {
      try {
        await changeSession((client) =>
          client.POST("v3/customer/authenticate", { ...credentials, expand: CUSTOMER_EXPAND }),
        )
      } catch (error) {
        if (error instanceof Spektrix.HttpError && error.status === 401) {
          throw new Error("Your email or password is incorrect. Please try again.")
        }
        throw new Error(errorMessage(error, "We couldn't sign you in. Please try again."))
      }
    },
    async createAccount(details: NewAccount) {
      try {
        await changeSession(async (client) => {
          // The generated type lists every customer field; the account form collects the essentials.
          await client.POST("v3/customer?domain={domain}", details as Spektrix.NewCustomer)
          // Sign in explicitly rather than relying on how Spektrix treats a newly created customer.
          return client.POST("v3/customer/authenticate", {
            email: details.email,
            password: details.password,
            expand: CUSTOMER_EXPAND,
          })
        })
      } catch (error) {
        throw new Error(errorMessage(error, "We couldn't create your account. Please try again."))
      }
    },
    /** The signed in customer's order history. */
    async loadOrders() {
      try {
        return await createClient().GET("v3/customer/orders", { expand: ORDER_EXPAND })
      } catch (error) {
        throw new Error(errorMessage(error, "We couldn't load your tickets. Please try again."))
      }
    },
    async updateDetails(details: CustomerDetails) {
      // A customer read already in flight would otherwise restore the old details.
      sessionRevision++
      try {
        // PATCH changes only the fields sent; the generated type lists every customer field.
        const customer = await createClient().PATCH("v3/customer", {
          ...(details as Spektrix.CustomerPatch),
          expand: CUSTOMER_EXPAND,
        })
        store.customer = customer
      } catch (error) {
        throw new Error(errorMessage(error, "We couldn't save your details. Please try again."))
      }
    },
    /**
     * Spektrix answers change-password the same way whether or not the current password is right, so this
     * checks the current password by signing in with it, then signs in with the new one to confirm the change.
     */
    async changePassword(oldPassword: string, newPassword: string) {
      const email = store.customer?.email
      if (!email) throw new Error("Please sign in again to change your password.")
      const client = createClient()
      const authenticate = (password: string) =>
        client.POST("v3/customer/authenticate", { email, password, expand: CUSTOMER_EXPAND })
      const isRejected = (error: unknown) => error instanceof Spektrix.HttpError && error.status === 401
      const fallback = "We couldn't change your password. Please try again."

      try {
        await authenticate(oldPassword)
      } catch (error) {
        if (!isRejected(error)) throw new Error(errorMessage(error, fallback))
        // Show the panel the session as Spektrix now sees it.
        void store.refreshCustomer()
        throw new Error("Your current password is incorrect.")
      }
      try {
        await client.POST("v3/customer/change-password", { oldPassword, newPassword })
        store.customer = await authenticate(newPassword)
      } catch (error) {
        if (!isRejected(error)) throw new Error(errorMessage(error, fallback))
        void store.refreshCustomer()
        throw new Error("Your password wasn't changed. Please try a different password.")
      }
    },
    /** Emails the customer a link to choose a new password. Unknown addresses are not reported. */
    async requestPasswordReset(email: string) {
      try {
        await createClient().POST("v3/customer/forgot-password?emailAddress={emailAddress}&domain={domain}", {
          emailAddress: email,
          // Selects the Website Admin domain config, whose Password Reset Page Link the email points to.
          domain: window.location.hostname,
        })
      } catch (error) {
        // Spektrix responds 404 for unknown addresses. Treat it as sent, so the form doesn't reveal who has an account.
        if (error instanceof Spektrix.HttpError && error.status === 404) return
        throw new Error(errorMessage(error, "We couldn't send your reset link. Please try again."))
      }
    },
    /** Checks a password reset token from an emailed link is still usable. */
    async isPasswordResetTokenValid(token: string) {
      try {
        return await createClient().POST("v3/customer/validate-password-link?token={token}", { token })
      } catch (error) {
        if (error instanceof Spektrix.HttpError && error.status === 401) return false
        throw new Error(errorMessage(error, "We couldn't check your reset link. Please try again."))
      }
    },
    async resetPassword(token: string, newPassword: string) {
      try {
        await createClient().POST("v3/customer/set-password", { token, newPassword })
      } catch (error) {
        if (error instanceof Spektrix.HttpError && error.status === 401) {
          throw new Error("This reset link is invalid or has expired. Please request a new one.")
        }
        throw new Error(errorMessage(error, "We couldn't reset your password. Please try again."))
      }
    },
    async signOut() {
      try {
        await changeSession(async (client) => {
          await client.POST("v3/customer/deauthenticate", {})
          return null
        })
      } catch (error) {
        throw new Error(errorMessage(error, "We couldn't sign you out. Please try again."))
      }
    },
  })

  return store
}

export const accountStore = createAccountStore()

export type AccountStore = typeof accountStore
